import { useState } from 'react';
import {
  Box, Button, TextField, Typography, Paper, Alert, Link,
  InputAdornment, IconButton
} from '@mui/material';
import { Email, ArrowBack, Key, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link as RouterLink } from 'react-router-dom';
import enhancedAuthService from '../../services/enhancedAuth';

//  Validation
const emailSchema = yup.object({ email: yup.string().email().required() });
const otpSchema = yup.object({ otp: yup.string().min(6).max(6).required() });
// Password rules: at least 8 chars, 1 uppercase, 1 number, 1 special (non-alphanumeric) char
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
// const PASSWORD_EXAMPLE = 'Abcd1234!';

const resetSchema = yup.object({
  password: yup.string().min(8).matches(PASSWORD_REGEX, 'Password must include 1 uppercase letter, 1 number and 1 special character').required(),
  confirm: yup.string().oneOf([yup.ref("password")], "Passwords do not match"),
});

export default function ForgotPasswordForm() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [msg, setMsg] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [show1,setShow1]=useState(false);
  const [show2,setShow2]=useState(false);

  const emailForm = useForm({ resolver: yupResolver(emailSchema) });
  const otpForm = useForm({ resolver: yupResolver(otpSchema) });
  const resetForm = useForm({ resolver: yupResolver(resetSchema) });

  // Step 1
  const sendEmail = async (v:any)=>{
    setLoading(true);
    try {
      const r = await enhancedAuthService.requestPasswordReset({ email: v.email });
      if (r?.success) {
        setEmail(v.email);
        setStep(2);
      }
      setMsg({
        type: r?.success ? 'success' : 'error',
        text: r?.message || (r?.success ? 'OTP sent to email' : 'User does not exist'),
      });
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.response?.data?.message || err?.message || 'Request failed' });
    } finally {
      setLoading(false);
    }
  };

  // Step 2
  const verifyOtp = async(v:any)=>{
    setLoading(true);
    try {
      const r = await enhancedAuthService.verifyResetOtp({ email, otp: v.otp });
      if (r?.success) {
        setOtp(v.otp);
        setStep(3);
      }
      setMsg({
        type: r?.success ? 'success' : 'error',
        text: r?.message || (r?.success ? 'OTP verified' : 'Invalid OTP or user not found'),
      });
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.response?.data?.message || err?.message || 'Verification failed' });
    } finally {
      setLoading(false);
    }
  };

  // Step 3
  const resetPass = async(v:any)=>{
    setLoading(true);
    try {
      // extra safety: ensure password meets pattern (in case validation bypassed)
      if (!PASSWORD_REGEX.test(v.password)) {
        setMsg({ type: 'error', text: "Password must be at least 8 characters and include 1 uppercase letter, 1 number and 1 special character. Example"});
        return;
      }

      const r = await enhancedAuthService.resetPassword({ email, otp, newPassword: v.password });
      setMsg({ type: r?.success ? 'success' : 'error', text: r?.message || (r?.success ? 'Password reset' : 'Failed to reset password') });
      if (r?.success) {
        // show success briefly then redirect to login
        setTimeout(() => (window.location.href = '/login'), 1500);
      }
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.response?.data?.message || err?.message || 'Failed to reset password' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight:"100vh",
      display:"flex",
      justifyContent:"center",
      alignItems:"center",
      background:"linear-gradient(180deg,#051429,#0A1E3A)",
      p:2
    }}>

      <Paper sx={{
        width:"100%", maxWidth:450, p:5,
        textAlign:"center",
        background:"rgba(255,255,255,0.05)",
        backdropFilter:"blur(18px)",
        borderRadius:"20px",
        boxShadow:"0px 0px 35px rgba(0,255,140,0.25)",
        transition:"0.3s",
        border:"1px solid rgba(0,255,120,0.15)",
        "&:hover":{boxShadow:"0px 0px 55px rgba(0,255,120,0.35)"}
      }}>

        <Typography variant="h4" sx={{
          color: "#4EF06B", fontWeight:800, letterSpacing:1, mb:1
        }}>
          Reset Password
        </Typography>

        <Typography sx={{color:"#D7FFE8",opacity:.75,mb:3,fontSize:15}}>
          {step===1 && "Enter your email to receive OTP"}
          {step===2 && "Enter the OTP sent to your email"}
          {step===3 && "Set a new password to continue"}
        </Typography>

        {msg && <Alert severity={msg.type} sx={{mb:3,borderRadius:2}}>{msg.text}</Alert>}

        {/* STEP 1 */}
        {step===1 &&
          <Box component="form" onSubmit={emailForm.handleSubmit(sendEmail)}>
            <Controller name="email" control={emailForm.control} render={({field,fieldState})=>(
              <TextField {...field} fullWidth label="Email Address"
                InputProps={{startAdornment:<InputAdornment position="start"><Email sx={{color:"#00FF9D"}}/></InputAdornment>}}
                sx={inputStyle} error={!!fieldState.error}
                helperText={fieldState.error?.message}/>
            )}/>
            <Button type="submit" fullWidth sx={btnStyle}>
              {loading?"Sending...":"Send OTP"}
            </Button>
          </Box>
        }

        {/* STEP 2 */}
        {step===2 &&
          <Box component="form" onSubmit={otpForm.handleSubmit(verifyOtp)}>
            <Controller name="otp" control={otpForm.control} render={({field,fieldState})=>(
              <TextField {...field} fullWidth label="Enter 6-digit OTP"
                inputProps={{maxLength:6}}
                InputProps={{startAdornment:<InputAdornment position="start"><Key sx={{color:"#00FF9D"}}/></InputAdornment>}}
                sx={inputStyle} error={!!fieldState.error}
                helperText={fieldState.error?.message}/>
            )}/>
            <Button type="submit" fullWidth sx={btnStyle}>
              Verify OTP
            </Button>
          </Box>
        }

        {/* STEP 3 */}
        {step===3 &&
          <Box component="form" onSubmit={resetForm.handleSubmit(resetPass)}>
            <Controller name="password" control={resetForm.control} render={({field,fieldState})=>(
              <>
                <TextField {...field} fullWidth label="New Password"
                  type={show1?"text":"password"} sx={inputStyle}
                  InputProps={{
                    startAdornment:<InputAdornment position="start"><Lock sx={{color:"#00FF9D"}}/></InputAdornment>,
                    endAdornment:<IconButton onClick={()=>setShow1(!show1)}>{show1?<VisibilityOff/>:<Visibility/>}</IconButton>
                  }} error={!!fieldState.error} helperText={fieldState.error?.message}/>

                {/* <Typography variant="caption" sx={{display:'block',textAlign:'left',color:'#CCEADC',mt:1}}>
                  Password must be at least 8 characters, include one uppercase letter, one number and one special character. Example: {PASSWORD_EXAMPLE}
                </Typography> */}
              </>
            )}/>

            <Controller name="confirm" control={resetForm.control} render={({field,fieldState})=>(
              <TextField {...field} fullWidth label="Confirm Password"
                type={show2?"text":"password"} sx={{...inputStyle,mt:2}}
                InputProps={{
                  startAdornment:<InputAdornment position="start"><Lock sx={{color:"#00FF9D"}}/></InputAdornment>,
                  endAdornment:<IconButton onClick={()=>setShow2(!show2)}>{show2?<VisibilityOff/>:<Visibility/>}</IconButton>
                }} error={!!fieldState.error} helperText={fieldState.error?.message}/>
            )}/>
            <Button type="submit" fullWidth sx={btnStyle}>
              Reset Password
            </Button>
          </Box>
        }

        <Link component={RouterLink} to="/login" sx={{
          color: "#4EF06B",display:"inline-flex",alignItems:"center",
          mt:3,fontSize:14,fontWeight:600,"&:hover":{textDecoration:"underline"}
        }}>
          <ArrowBack sx={{mr:1}}/> Back to Login
        </Link>

      </Paper>
    </Box>
  );
}

/************************* UI STYLE BLOCKS *************************/
const inputStyle = {
  bgcolor:"rgba(255,255,255,0.08)",
  borderRadius:2,
  input:{color:"#CCFFE2"},
  mb:3,
  "& .MuiInputLabel-root":{color:"#8DFFC8"},
  "& fieldset":{border:"1px solid rgba(0,255,150,.3)"},
  "&:hover fieldset":{border:"1px solid #00FF9D"},
};

const btnStyle = {
  mt:1,py:1.4,fontWeight:700,fontSize:16,
  background: "linear-gradient(90deg,#0b6ef6,#26e064)",
  color:"#00332D",borderRadius:2,
  letterSpacing:1,
  "&:hover":{opacity:.85}
};